import React, { useState, useMemo } from "react";
import { Card, Select, Space, Typography, DatePicker, Spin, Collapse, InputNumber, Button, message, Popconfirm } from "antd";
import { Calendar, BarChart3, Edit2, Save, X, RotateCcw, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useQueries } from "@tanstack/react-query";
import { useGetTodayMetrics, useGetIndividualDailyMetrics } from "~/services/service-ticket";
import { useUpdateDailyMetricsOverrides, useSyncDailyMetrics } from "~/services/service-ticket/service-ticket.mutations";
import { useGetExpenseStats } from "~/services/expense";
import { serviceTicketService } from "~/services/service-ticket/service-ticket.services";

dayjs.extend(utc);
dayjs.extend(quarterOfYear);

const { Text } = Typography;

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

type DatePeriod = "day" | "week" | "month" | "quarter";

interface DailyReportRow {
  key: string;
  date: string;
  // Revenue and Profit
  revenue: number;
  profit: number;
  needsSync?: boolean;
  // Client services
  transport: { count: number; revenue: number; cost: number };
  site: { count: number; revenue: number; cost: number };
  fabrication: { count: number; revenue: number; cost: number };
  warehouse: { count: number; revenue: number; cost: number };
  // Staff details
  staffWorking: number;
  staffHours: number;
  staffCost: number;
  // Vessel details
  totalVessels: number;
  vesselHours: number;
  vesselCost: number;
  // Material details
  materialCharges: number;
  materialCosts: number;
}

interface DailyMetricsEntity {
  id: string;
  date: Date | string; // Can be Date object or ISO string from API
  totalStaffWorking: number;
  totalStaffHours: number;
  totalStaffCost: number;
  staffHoursOverride?: number | null;
  totalVessels: number;
  totalVesselHours: number;
  totalVesselCost: number;
  vesselHoursOverride?: number | null;
  transport: { count: number; revenue: number; cost: number };
  site: { count: number; revenue: number; cost: number };
  fabrication: { count: number; revenue: number; cost: number };
  warehouse: { count: number; revenue: number; cost: number };
  totalMaterialCharges: number;
  totalMaterialsCost: number;
  materialsCostOverride?: number | null;
  totalRevenue: number;
  totalDirectCosts: number;
  profit: number;
}

export const DailyReportsTab: React.FC = () => {
  const [datePeriod, setDatePeriod] = useState<DatePeriod>("day");
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [overrideValues, setOverrideValues] = useState<{
    staffHours?: number | null;
    vesselHours?: number | null;
    materialsCost?: number | null;
  }>({});
  
  const updateOverridesMutation = useUpdateDailyMetricsOverrides();
  const { mutate: syncMetrics, isPending: isSyncing } = useSyncDailyMetrics();

  // Calculate date range based on period
  const { startDate, endDate, dates } = useMemo(() => {
    const selected = dayjs(selectedDate);
    let start: Dayjs;
    let end: Dayjs;
    const dateList: string[] = [];

    switch (datePeriod) {
      case "day":
        start = selected.startOf("day");
        end = selected.endOf("day");
        dateList.push(selected.format("YYYY-MM-DD"));
        break;
      case "week":
        start = selected.startOf("week");
        end = selected.endOf("week");
        let current = start;
        while (current.isBefore(end) || current.isSame(end, "day")) {
          dateList.push(current.format("YYYY-MM-DD"));
          current = current.add(1, "day");
        }
        break;
      case "month":
        start = selected.startOf("month");
        end = selected.endOf("month");
        let monthCurrent = start;
        while (monthCurrent.isBefore(end) || monthCurrent.isSame(end, "day")) {
          dateList.push(monthCurrent.format("YYYY-MM-DD"));
          monthCurrent = monthCurrent.add(1, "day");
        }
        break;
      case "quarter":
        start = selected.startOf("quarter");
        end = selected.endOf("quarter");
        let quarterCurrent = start;
        while (quarterCurrent.isBefore(end) || quarterCurrent.isSame(end, "day")) {
          dateList.push(quarterCurrent.format("YYYY-MM-DD"));
          quarterCurrent = quarterCurrent.add(1, "day");
        }
        break;
    }

    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      dates: dateList,
    };
  }, [datePeriod, selectedDate]);

  // Fetch aggregated metrics for totals
  const { data: metricsData, isLoading: isLoadingAggregated } = useGetTodayMetrics(
    undefined,
    startDate,
    endDate
  );

  // Fetch individual daily metrics for week/month/quarter, or for single day if editing
  const { data: individualMetrics, isLoading: isLoadingIndividual } = useGetIndividualDailyMetrics(
    startDate,
    endDate
  );

  // Helper to normalize date to YYYY-MM-DD string consistently
  // This ensures dates from the backend (Date objects or ISO strings) match our dates array
  const normalizeDateStr = (date: Date | string | undefined | null): string => {
    if (!date) return '';
    if (typeof date === 'string') {
      // Extract just the date part (YYYY-MM-DD) from ISO string
      return date.split('T')[0];
    }
    if (date instanceof Date) {
      // Use UTC to match backend storage (PostgreSQL DATE is timezone-agnostic)
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Fallback: try to parse as date
    try {
      const d = new Date(date as any);
      if (!isNaN(d.getTime())) {
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      // Ignore
    }
    return '';
  };

  // Helper to check if service type has meaningful data
  const hasServiceData = (service: { count: number; revenue: number; cost: number } | undefined) => {
    return service && (service.revenue > 0 || service.count > 0);
  };

  // Identify days that need their metrics fetched (have revenue but empty service types)
  const daysNeedingMetrics = useMemo(() => {
    if (datePeriod === "day") {
      return []; // Single day uses aggregated metrics directly
    }
    
    const daysToFetch: string[] = [];
    const metricsMap = new Map<string, DailyMetricsEntity>();
    
    // Build map of existing database records
    if (individualMetrics && individualMetrics.length > 0) {
      individualMetrics.forEach((m: DailyMetricsEntity) => {
        const dateStr = normalizeDateStr(m.date);
        if (dateStr) {
          metricsMap.set(dateStr, m);
        }
      });
    }
    
    // Check each date in the period
    dates.forEach((dateStr) => {
      const m = metricsMap.get(dateStr);
      const dayRevenue = m ? Number(m.totalRevenue || 0) : 0;
      
      // Fetch metrics if:
      // 1. Day has revenue but empty service types/hours in database, OR
      // 2. Day has no database record but we're in a period view (might have revenue from aggregated)
      if (m && dayRevenue > 0) {
        // Check if service types are empty
        const hasAnyServiceData = hasServiceData(m.transport) || 
                                 hasServiceData(m.site) || 
                                 hasServiceData(m.fabrication) || 
                                 hasServiceData(m.warehouse);
        
        // Check if hours are missing
        const hasHours = Number(m.totalStaffHours || 0) > 0 || Number(m.totalVesselHours || 0) > 0;
        
        // If revenue exists but service types or hours are missing, fetch metrics
        if (!hasAnyServiceData || !hasHours) {
          daysToFetch.push(dateStr);
        }
      } else if (!m && datePeriod !== "day") {
        // For days without database records in multi-day view, fetch to see if there's actual data
        // (This helps populate days that haven't been synced yet)
        daysToFetch.push(dateStr);
      }
    });
    
    return daysToFetch;
  }, [individualMetrics, datePeriod, dates]);

  // Fetch metrics for days that need them
  const dayMetricsQueries = useQueries({
    queries: daysNeedingMetrics.map((dateStr) => ({
      queryKey: ['service-tickets', 'today-metrics', dateStr],
      queryFn: () => serviceTicketService.getTodayMetrics(dateStr),
      staleTime: 1 * 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Create a map of fetched day metrics
  const fetchedDayMetricsMap = useMemo(() => {
    const map = new Map<string, typeof metrics>();
    dayMetricsQueries.forEach((query, index) => {
      if (query.data && daysNeedingMetrics[index]) {
        map.set(daysNeedingMetrics[index], query.data);
      }
    });
    return map;
  }, [dayMetricsQueries, daysNeedingMetrics]);

  // Expense stats (for other expenses)
  const { data: expenseStats } = useGetExpenseStats();

  const metrics = metricsData || {
    totalStaffWorking: 0,
    transport: { count: 0, revenue: 0, cost: 0 },
    site: { count: 0, revenue: 0, cost: 0 },
    fabrication: { count: 0, revenue: 0, cost: 0 },
    warehouse: { count: 0, revenue: 0, cost: 0 },
    totalMaterialCharges: 0,
    totalStaffHours: 0,
    totalVesselHours: 0,
    totalVessels: 0,
    totalStaffCost: 0,
    totalVesselCost: 0,
    totalMaterialsCost: 0,
    totalDirectCosts: 0,
    totalRevenue: 0,
    totalCost: 0,
    profit: 0,
  };

  const otherExpenses = expenseStats?.paidAmount || 0;
  const totalDirectExpenses = metrics.totalDirectCosts + otherExpenses;
  const profit = metrics.totalRevenue - totalDirectExpenses;

  // Helper to get the metric entity for a date
  const getMetricEntity = (dateStr: string): DailyMetricsEntity | undefined => {
    if (individualMetrics && individualMetrics.length > 0) {
      return individualMetrics.find((m: DailyMetricsEntity) => {
        const dateStrFromMetric = normalizeDateStr(m.date);
        return dateStrFromMetric === dateStr;
      });
    }
    return undefined;
  };

  // Convert metrics to single row per day
  const tableData: DailyReportRow[] = useMemo(() => {
    const rows: DailyReportRow[] = [];

    if (datePeriod === "day") {
      // Single day - check if we have individual metrics with overrides
      const singleDayMetric = individualMetrics && individualMetrics.length > 0
        ? individualMetrics.find((m: DailyMetricsEntity) => {
            const dateStrFromMetric = normalizeDateStr(m.date);
            return dateStrFromMetric === selectedDate;
          })
        : undefined;
      
      // Revenue doesn't change with overrides, so always use aggregated metrics for revenue
      // Use individual metric for costs/profit (which are recalculated when overrides are set)
      // BUT: If database costs are 0 or missing, fall back to aggregated metrics
      const effectiveRevenue = metrics.totalRevenue;
      const effectiveStaffCost = singleDayMetric && singleDayMetric.totalStaffCost !== undefined && Number(singleDayMetric.totalStaffCost) > 0
        ? Number(singleDayMetric.totalStaffCost)
        : metrics.totalStaffCost;
      const effectiveVesselCost = singleDayMetric && singleDayMetric.totalVesselCost !== undefined && Number(singleDayMetric.totalVesselCost) > 0
        ? Number(singleDayMetric.totalVesselCost)
        : metrics.totalVesselCost;
      const effectiveMaterialsCost = singleDayMetric
        ? (singleDayMetric.materialsCostOverride !== null && singleDayMetric.materialsCostOverride !== undefined
            ? Number(singleDayMetric.materialsCostOverride)
            : (singleDayMetric.totalMaterialsCost !== undefined && Number(singleDayMetric.totalMaterialsCost) > 0 
                ? Number(singleDayMetric.totalMaterialsCost) 
                : metrics.totalMaterialsCost))
        : metrics.totalMaterialsCost;
      const effectiveDirectCosts = effectiveStaffCost + effectiveVesselCost + effectiveMaterialsCost;
      // Always calculate profit from revenue and costs (don't trust database profit field)
      // Profit = Revenue - Direct Costs - Other Expenses
      const effectiveProfit = effectiveRevenue - effectiveDirectCosts - otherExpenses;
      
      // Detect data mismatch: revenue exists but hours/costs are 0 (needs sync)
      const hasDataMismatch = effectiveRevenue > 0 && effectiveDirectCosts === 0 && (
        (singleDayMetric && Number(singleDayMetric.totalStaffHours || 0) === 0 && Number(singleDayMetric.totalVesselHours || 0) === 0 && Number(singleDayMetric.totalMaterialsCost || 0) === 0) ||
        (!singleDayMetric && metrics.totalStaffHours === 0 && metrics.totalVesselHours === 0 && metrics.totalMaterialsCost === 0)
      );
      
      // Helper to check if service type has meaningful data
      const hasServiceData = (service: { count: number; revenue: number; cost: number } | undefined) => {
        return service && (service.revenue > 0 || service.count > 0);
      };
      
      rows.push({
        key: selectedDate,
        date: selectedDate,
        revenue: effectiveRevenue,
        profit: effectiveProfit,
        needsSync: hasDataMismatch,
        transport: hasServiceData(singleDayMetric?.transport) ? singleDayMetric!.transport : metrics.transport,
        site: hasServiceData(singleDayMetric?.site) ? singleDayMetric!.site : metrics.site,
        fabrication: hasServiceData(singleDayMetric?.fabrication) ? singleDayMetric!.fabrication : metrics.fabrication,
        warehouse: hasServiceData(singleDayMetric?.warehouse) ? singleDayMetric!.warehouse : metrics.warehouse,
        staffWorking: singleDayMetric?.totalStaffWorking || metrics.totalStaffWorking,
        staffHours: singleDayMetric 
          ? (singleDayMetric.staffHoursOverride !== null && singleDayMetric.staffHoursOverride !== undefined 
              ? Number(singleDayMetric.staffHoursOverride) 
              : (Number(singleDayMetric.totalStaffHours || 0) > 0 
                  ? Number(singleDayMetric.totalStaffHours) 
                  : metrics.totalStaffHours))
          : metrics.totalStaffHours,
        staffCost: effectiveStaffCost,
        totalVessels: singleDayMetric?.totalVessels || metrics.totalVessels || 0,
        vesselHours: singleDayMetric
          ? (singleDayMetric.vesselHoursOverride !== null && singleDayMetric.vesselHoursOverride !== undefined
              ? Number(singleDayMetric.vesselHoursOverride)
              : (Number(singleDayMetric.totalVesselHours || 0) > 0
                  ? Number(singleDayMetric.totalVesselHours)
                  : metrics.totalVesselHours))
          : metrics.totalVesselHours,
        vesselCost: effectiveVesselCost,
        materialCharges: singleDayMetric && Number(singleDayMetric.totalMaterialCharges || 0) > 0
          ? Number(singleDayMetric.totalMaterialCharges)
          : metrics.totalMaterialCharges,
        materialCosts: effectiveMaterialsCost,
      });
    } else {
      // Multiple days - ensure ALL dates in the period are shown
      // Create a map of existing metrics by date
      const metricsMap = new Map<string, DailyMetricsEntity>();
      if (individualMetrics && individualMetrics.length > 0) {
        individualMetrics.forEach((m: DailyMetricsEntity) => {
          const dateStr = normalizeDateStr(m.date);
          if (dateStr) {
            metricsMap.set(dateStr, m);
          }
        });
      }

      // Create rows for ALL dates in the range
      // Only use database records - don't distribute metrics evenly (revenue isn't evenly distributed)
      // Days without database records will show 0 values

      dates.forEach((dateStr) => {
        const m = metricsMap.get(dateStr);
        
        // If we have a database record, use its values, but fall back to aggregated metrics if values are empty/zero
        if (m) {
          // Use database record values - check for overrides first, then use stored values
          const dayRevenue = Number(m.totalRevenue || 0);
          const dayStaffCost = Number(m.totalStaffCost || 0);
          const dayVesselCost = Number(m.totalVesselCost || 0);
          const dayMaterialsCost = m.materialsCostOverride !== null && m.materialsCostOverride !== undefined
            ? Number(m.materialsCostOverride)
            : Number(m.totalMaterialsCost || 0);
          const dayDirectCosts = dayStaffCost + dayVesselCost + dayMaterialsCost;
          const dayOtherExpenses = otherExpenses / dates.length;
          const dayProfit = dayRevenue - dayDirectCosts - dayOtherExpenses;
          
          // Detect data mismatch: revenue exists but direct costs are 0 (needs sync)
          const hasDataMismatch = dayRevenue > 0 && dayDirectCosts === 0 && 
            Number(m.totalStaffHours || 0) === 0 && 
            Number(m.totalVesselHours || 0) === 0 && 
            Number(m.totalMaterialsCost || 0) === 0;
          
          // Get fetched metrics for this day if available (for multi-day periods with incomplete data)
          const fetchedDayMetrics = fetchedDayMetricsMap.get(dateStr);
          
          // For single day view, use aggregated metrics if database record is incomplete
          // For multi-day, use fetched day-specific metrics if available, otherwise use database values
          const useAggregatedForServiceTypes = datePeriod === "day";
          
          // Helper to get service data: prefer fetched metrics if available and database is empty, 
          // otherwise use aggregated (for single day) or database values
          const getServiceData = (
            dbService: { count: number; revenue: number; cost: number } | undefined,
            serviceName: 'transport' | 'site' | 'fabrication' | 'warehouse'
          ) => {
            // If we have fetched day metrics and database service is empty, use fetched
            if (fetchedDayMetrics && !hasServiceData(dbService)) {
              return fetchedDayMetrics[serviceName] || { count: 0, revenue: 0, cost: 0 };
            }
            // For single day, use aggregated if database is empty
            if (useAggregatedForServiceTypes && !hasServiceData(dbService)) {
              return metrics[serviceName] || { count: 0, revenue: 0, cost: 0 };
            }
            // Otherwise use database value
            return dbService || { count: 0, revenue: 0, cost: 0 };
          };
          
          rows.push({
            key: dateStr,
            date: dateStr,
            revenue: dayRevenue,
            profit: dayProfit,
            needsSync: hasDataMismatch,
            transport: getServiceData(m.transport, 'transport'),
            site: getServiceData(m.site, 'site'),
            fabrication: getServiceData(m.fabrication, 'fabrication'),
            warehouse: getServiceData(m.warehouse, 'warehouse'),
            staffWorking: fetchedDayMetrics?.totalStaffWorking || m.totalStaffWorking || metrics.totalStaffWorking || 0,
            staffHours: m.staffHoursOverride !== null && m.staffHoursOverride !== undefined
              ? Number(m.staffHoursOverride)
              : (Number(m.totalStaffHours || 0) > 0 
                  ? Number(m.totalStaffHours)
                  : (fetchedDayMetrics?.totalStaffHours || (useAggregatedForServiceTypes ? metrics.totalStaffHours : 0))),
            staffCost: dayStaffCost,
            totalVessels: fetchedDayMetrics?.totalVessels || m.totalVessels || metrics.totalVessels || 0,
            vesselHours: m.vesselHoursOverride !== null && m.vesselHoursOverride !== undefined
              ? Number(m.vesselHoursOverride)
              : (Number(m.totalVesselHours || 0) > 0
                  ? Number(m.totalVesselHours)
                  : (fetchedDayMetrics?.totalVesselHours || (useAggregatedForServiceTypes ? metrics.totalVesselHours : 0))),
            vesselCost: dayVesselCost,
            materialCharges: Number(m.totalMaterialCharges || 0) > 0
              ? Number(m.totalMaterialCharges)
              : (fetchedDayMetrics?.totalMaterialCharges || (useAggregatedForServiceTypes ? metrics.totalMaterialCharges : 0)),
            materialCosts: dayMaterialsCost,
          });
        } else {
          // No database record - check if we have fetched metrics for this day
          const fetchedDayMetrics = fetchedDayMetricsMap.get(dateStr);
          
          if (fetchedDayMetrics) {
            // Use fetched metrics if available
            const dayRevenue = fetchedDayMetrics.totalRevenue || 0;
            const dayStaffCost = fetchedDayMetrics.totalStaffCost || 0;
            const dayVesselCost = fetchedDayMetrics.totalVesselCost || 0;
            const dayMaterialsCost = fetchedDayMetrics.totalMaterialsCost || 0;
            const dayDirectCosts = dayStaffCost + dayVesselCost + dayMaterialsCost;
            const dayOtherExpenses = otherExpenses / dates.length;
            const dayProfit = dayRevenue - dayDirectCosts - dayOtherExpenses;
            
            rows.push({
              key: dateStr,
              date: dateStr,
              revenue: dayRevenue,
              profit: dayProfit,
              needsSync: false,
              transport: fetchedDayMetrics.transport || { count: 0, revenue: 0, cost: 0 },
              site: fetchedDayMetrics.site || { count: 0, revenue: 0, cost: 0 },
              fabrication: fetchedDayMetrics.fabrication || { count: 0, revenue: 0, cost: 0 },
              warehouse: fetchedDayMetrics.warehouse || { count: 0, revenue: 0, cost: 0 },
              staffWorking: fetchedDayMetrics.totalStaffWorking || 0,
              staffHours: fetchedDayMetrics.totalStaffHours || 0,
              staffCost: dayStaffCost,
              totalVessels: fetchedDayMetrics.totalVessels || 0,
              vesselHours: fetchedDayMetrics.totalVesselHours || 0,
              vesselCost: dayVesselCost,
              materialCharges: fetchedDayMetrics.totalMaterialCharges || 0,
              materialCosts: dayMaterialsCost,
            });
          } else {
            // No database record and no fetched metrics - show 0 values
            const dayOtherExpenses = otherExpenses / dates.length;
            const dayProfit = 0 - dayOtherExpenses; // Only other expenses, no revenue or costs
            
            rows.push({
              key: dateStr,
              date: dateStr,
              revenue: 0,
              profit: dayProfit,
              needsSync: false,
              transport: { count: 0, revenue: 0, cost: 0 },
              site: { count: 0, revenue: 0, cost: 0 },
              fabrication: { count: 0, revenue: 0, cost: 0 },
              warehouse: { count: 0, revenue: 0, cost: 0 },
              staffWorking: 0,
              staffHours: 0,
              staffCost: 0,
              totalVessels: 0,
              vesselHours: 0,
              vesselCost: 0,
              materialCharges: 0,
              materialCosts: 0,
            });
          }
        }
      });
    }

    return rows;
  }, [datePeriod, selectedDate, metrics, individualMetrics, profit, otherExpenses, dates, fetchedDayMetricsMap]);

  // Calculate totals across all days
  // Use aggregated metrics (live calculation) instead of summing individual rows
  // This ensures totals match the top summary cards even if some days are missing from database
  const totals = useMemo(() => {
    if (!metrics) {
      return null;
    }

    // Use the aggregated metrics directly (from live calculation)
    // This matches what's shown in the top summary cards
    const aggregated = {
      revenue: metrics.totalRevenue,
      profit: metrics.totalRevenue - (metrics.totalStaffCost + metrics.totalVesselCost + metrics.totalMaterialsCost + otherExpenses),
      transport: metrics.transport,
      site: metrics.site,
      fabrication: metrics.fabrication,
      warehouse: metrics.warehouse,
      staffWorking: metrics.totalStaffWorking,
      staffHours: metrics.totalStaffHours,
      staffCost: metrics.totalStaffCost,
      totalVessels: metrics.totalVessels || 0,
      vesselHours: metrics.totalVesselHours,
      vesselCost: metrics.totalVesselCost,
      materialCharges: metrics.totalMaterialCharges,
      materialCosts: metrics.totalMaterialsCost,
    };

    return aggregated;
  }, [metrics, otherExpenses]);

  const isLoading = isLoadingAggregated || (datePeriod !== "day" && isLoadingIndividual);

  const handleEdit = (dateStr: string) => {
    const metric = getMetricEntity(dateStr);
    setEditingDate(dateStr);
    setOverrideValues({
      staffHours: metric?.staffHoursOverride ?? null,
      vesselHours: metric?.vesselHoursOverride ?? null,
      materialsCost: metric?.materialsCostOverride ?? null,
    });
  };

  const handleCancel = () => {
    setEditingDate(null);
    setOverrideValues({});
  };

  const handleSave = async (dateStr: string) => {
    try {
      // Always send all three fields - convert to numbers and handle null
      const overrides = {
        staffHoursOverride: overrideValues.staffHours !== undefined && overrideValues.staffHours !== null 
          ? Number(overrideValues.staffHours) 
          : overrideValues.staffHours === null ? null : undefined,
        vesselHoursOverride: overrideValues.vesselHours !== undefined && overrideValues.vesselHours !== null 
          ? Number(overrideValues.vesselHours) 
          : overrideValues.vesselHours === null ? null : undefined,
        materialsCostOverride: overrideValues.materialsCost !== undefined && overrideValues.materialsCost !== null 
          ? Number(overrideValues.materialsCost) 
          : overrideValues.materialsCost === null ? null : undefined,
      };
      
      console.log('Saving overrides:', { date: dateStr, overrides });
      
      await updateOverridesMutation.mutateAsync({
        date: dateStr,
        overrides,
      });
      message.success("Overrides saved successfully");
      setEditingDate(null);
      setOverrideValues({});
    } catch (error) {
      message.error("Failed to save overrides");
      console.error("Error saving overrides:", error);
    }
  };

  const handleSync = async (dateStr: string) => {
    try {
      await new Promise<void>((resolve, reject) => {
        syncMetrics(
          { date: dateStr },
          {
            onSuccess: () => {
              message.success(`Metrics synced for ${dayjs(dateStr).format("MMM D, YYYY")}`);
              resolve();
            },
            onError: (error) => {
              message.error(`Failed to sync metrics: ${error.message || "Unknown error"}`);
              reject(error);
            },
          }
        );
      });
    } catch (error) {
      // Error already handled in onError
    }
  };

  const handleRevert = async (dateStr: string) => {
    try {
      // Set all overrides to null to revert to calculated values
      const overrides = {
        staffHoursOverride: null,
        vesselHoursOverride: null,
        materialsCostOverride: null,
      };
      
      await updateOverridesMutation.mutateAsync({
        date: dateStr,
        overrides,
      });
      message.success("Overrides reverted successfully");
      setEditingDate(null);
      setOverrideValues({});
    } catch (error) {
      message.error("Failed to revert overrides");
      console.error("Error reverting overrides:", error);
    }
  };

  // Check if a date has any overrides set
  const hasOverrides = (dateStr: string): boolean => {
    const metric = getMetricEntity(dateStr);
    if (!metric) return false;
    return (
      (metric.staffHoursOverride !== null && metric.staffHoursOverride !== undefined) ||
      (metric.vesselHoursOverride !== null && metric.vesselHoursOverride !== undefined) ||
      (metric.materialsCostOverride !== null && metric.materialsCostOverride !== undefined)
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-gray-200">
          <div className="space-y-2">
            <div className="text-xs text-gray-500">Period Revenue</div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatCurrency(metrics.totalRevenue)}
            </div>
            <div className="text-xs text-gray-500">
              {datePeriod === "day" ? "Today" : `${dates.length} days`}
            </div>
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="space-y-2">
            <div className="text-xs text-gray-500">Direct Expenses</div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatCurrency(totalDirectExpenses)}
            </div>
            <div className="text-xs text-gray-500">
              Staff: {formatCurrency(metrics.totalStaffCost)} • 
              Vessel: {formatCurrency(metrics.totalVesselCost)} • 
              Materials: {formatCurrency(metrics.totalMaterialsCost)}
            </div>
          </div>
        </Card>
        <Card className="border-gray-200">
          <div className="space-y-2">
            <div className="text-xs text-gray-500">Est. Profit</div>
            <div className={`text-2xl font-semibold ${profit >= 0 ? "text-gray-900" : "text-red-600"}`}>
              {formatCurrency(profit)}
            </div>
            <div className="text-xs text-gray-500">
              {profit >= 0 ? "Positive" : "Negative"}
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-gray-200">
        <Space size="large" className="w-full" wrap>
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <Text className="text-sm font-medium">Period:</Text>
            <Select
              value={datePeriod}
              onChange={(value) => {
                setDatePeriod(value);
                setSelectedDate(dayjs().format("YYYY-MM-DD"));
              }}
              style={{ width: 120 }}
              options={[
                { label: "Day", value: "day" },
                { label: "Week", value: "week" },
                { label: "Month", value: "month" },
                { label: "Quarter", value: "quarter" },
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <Text className="text-sm font-medium">Date:</Text>
            <div className="flex items-center gap-1 border border-gray-300 rounded-md overflow-hidden">
              <Button
                type="text"
                size="small"
                icon={<ChevronLeft size={14} />}
                onClick={() => {
                  const current = dayjs(selectedDate);
                  let newDate: Dayjs;
                  if (datePeriod === "day") {
                    newDate = current.subtract(1, "day");
                  } else if (datePeriod === "week") {
                    newDate = current.subtract(1, "week").startOf("week");
                  } else if (datePeriod === "month") {
                    newDate = current.subtract(1, "month").startOf("month");
                  } else if (datePeriod === "quarter") {
                    newDate = current.subtract(1, "quarter").startOf("quarter");
                  } else {
                    newDate = current.subtract(1, "day");
                  }
                  setSelectedDate(newDate.format("YYYY-MM-DD"));
                }}
                className="border-r border-gray-300 rounded-none"
                title="Previous period"
              />
              <DatePicker
                value={dayjs(selectedDate)}
                onChange={(date) => {
                  if (date) {
                    let dateToSet: Dayjs;
                    if (datePeriod === "week") {
                      dateToSet = date.startOf("week");
                    } else if (datePeriod === "month") {
                      dateToSet = date.startOf("month");
                    } else if (datePeriod === "quarter") {
                      dateToSet = date.startOf("quarter");
                    } else {
                      dateToSet = date;
                    }
                    setSelectedDate(dateToSet.format("YYYY-MM-DD"));
                  }
                }}
                picker={datePeriod === "day" ? "date" : datePeriod === "week" ? "week" : "month"}
                format={datePeriod === "day" ? "MMM D, YYYY" : datePeriod === "week" ? "MMM D, YYYY [Week] W" : "MMM YYYY"}
                className="!border-0 !shadow-none"
                style={{ width: 180 }}
              />
              <Button
                type="text"
                size="small"
                icon={<ChevronRight size={14} />}
                onClick={() => {
                  const current = dayjs(selectedDate);
                  let newDate: Dayjs;
                  if (datePeriod === "day") {
                    newDate = current.add(1, "day");
                  } else if (datePeriod === "week") {
                    newDate = current.add(1, "week").startOf("week");
                  } else if (datePeriod === "month") {
                    newDate = current.add(1, "month").startOf("month");
                  } else if (datePeriod === "quarter") {
                    newDate = current.add(1, "quarter").startOf("quarter");
                  } else {
                    newDate = current.add(1, "day");
                  }
                  setSelectedDate(newDate.format("YYYY-MM-DD"));
                }}
                className="border-l border-gray-300 rounded-none"
                title="Next period"
              />
            </div>
          </div>
          {datePeriod !== "day" && (
            <div className="flex items-center gap-2">
              <Text className="text-sm text-gray-500">Range:</Text>
              <Text className="text-sm font-medium text-gray-700">
                {dayjs(startDate).format("MMM D")} - {dayjs(endDate).format("MMM D, YYYY")}
              </Text>
            </div>
          )}
        </Space>
      </Card>

      {/* Stacked Data Cards */}
      <Card
        className="border-gray-200"
        title={
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-gray-600" />
            <span className="text-gray-900 font-semibold">Daily Reports</span>
            <Text type="secondary" className="text-sm font-normal">
              ({dates.length} {datePeriod === "day" ? "day" : "days"})
            </Text>
          </div>
        }
      >
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Totals Card */}
            {totals && (
              <Card
                className="border-2 border-blue-500 bg-blue-50 shadow-lg"
                bodyStyle={{ padding: '20px' }}
              >
                <div className="space-y-4">
                  {/* Header Row */}
                  <div className="flex items-center justify-between border-b-2 border-blue-300 pb-3">
                    <div className="text-xl font-bold text-gray-900">
                      PERIOD TOTALS ({dates.length} {datePeriod === "day" ? "day" : "days"})
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-gray-600 font-medium">Total Revenue</div>
                        <div className="text-xl font-bold text-gray-900">{formatCurrency(totals.revenue)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600 font-medium">Total Est. Profit</div>
                        <div className={`text-xl font-bold ${totals.profit >= 0 ? "text-gray-900" : "text-red-600"}`}>
                          {formatCurrency(totals.profit)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Client Services Row */}
                  <div className="grid grid-cols-4 gap-0 border-b border-blue-200 pb-4">
                    <div className="text-center border-r border-blue-200 pr-4">
                      <div className="text-xs text-gray-600 font-medium mb-1">Transport</div>
                      <div className="font-bold text-gray-900">{formatCurrency(totals.transport.revenue)}</div>
                      <div className="text-xs text-gray-600">{totals.transport.count} services</div>
                    </div>
                    <div className="text-center border-r border-blue-200 pr-2 pl-2 bg-blue-100">
                      <div className="text-xs text-gray-700 font-semibold mb-1">Site Services</div>
                      <div className="font-bold text-gray-900">{formatCurrency(totals.site.revenue)}</div>
                      <div className="text-xs text-gray-700">{totals.site.count} services</div>
                    </div>
                    <div className="text-center border-r border-blue-200 pr-4 pl-2 bg-blue-100">
                      <div className="text-xs text-gray-700 font-semibold mb-1">Fabrication</div>
                      <div className="font-bold text-gray-900">{formatCurrency(totals.fabrication.revenue)}</div>
                      <div className="text-xs text-gray-700">{totals.fabrication.count} services</div>
                    </div>
                    <div className="text-center pl-4">
                      <div className="text-xs text-gray-600 font-medium mb-1">Warehouse</div>
                      <div className="font-bold text-gray-900">{formatCurrency(totals.warehouse.revenue)}</div>
                      <div className="text-xs text-gray-600">{totals.warehouse.count} services</div>
                    </div>
                  </div>

                  {/* Costs Row */}
                  <div className="grid grid-cols-4 gap-6">
                    {/* Staff */}
                    <div className="border-r border-blue-200 pr-6">
                      <div className="text-sm font-bold text-gray-800 mb-2">Staff</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Max Working:</span>
                          <span className="font-bold">{totals.staffWorking}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Hours:</span>
                          <span className="font-bold">{totals.staffHours.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Cost:</span>
                          <span className="font-bold">{formatCurrency(totals.staffCost)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Vessels */}
                    <div className="border-r border-blue-200 pr-6">
                      <div className="text-sm font-bold text-gray-800 mb-2">Vessels</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Max Total:</span>
                          <span className="font-bold">{totals.totalVessels}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Hours:</span>
                          <span className="font-bold">{totals.vesselHours.toFixed(1)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Cost:</span>
                          <span className="font-bold">{formatCurrency(totals.vesselCost)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Materials */}
                    <div className="border-r border-blue-200 pr-6">
                      <div className="text-sm font-bold text-gray-800 mb-2">Materials</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Charges:</span>
                          <span className="font-bold">{formatCurrency(totals.materialCharges)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Costs:</span>
                          <span className="font-bold">{formatCurrency(totals.materialCosts)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Additional Expenses & Commissions */}
                    <div>
                      <div className="text-sm font-bold text-gray-800 mb-2">Other Expenses</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Additional:</span>
                          <span className="font-bold">{formatCurrency(otherExpenses)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Commissions:</span>
                          <span className="font-bold">{formatCurrency(0)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                          <span className="text-gray-700 font-bold">Total Other:</span>
                          <span className="font-bold text-gray-900">{formatCurrency(otherExpenses)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Individual Day Cards - Collapsible */}
            <Collapse
              ghost
              items={tableData.map((row) => ({
                key: row.key,
                label: (
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="text-base font-semibold text-gray-900">
                      {dayjs(row.date).format("MMM D, YYYY")}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Revenue</div>
                        <div className="text-base font-semibold">{formatCurrency(row.revenue)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Est. Profit</div>
                        <div className={`text-base font-semibold ${row.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {row.profit >= 0 ? '+' : ''}{formatCurrency(row.profit)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {row.needsSync && editingDate !== row.date && (
                          <Popconfirm
                            title="Sync metrics for this day?"
                            description="This will recalculate hours and costs from service tickets for this date."
                            onConfirm={(e) => {
                              e?.stopPropagation();
                              handleSync(row.date);
                            }}
                            onCancel={(e) => e?.stopPropagation()}
                            okText="Yes, Sync"
                            cancelText="Cancel"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<RefreshCw size={14} className={isSyncing ? "animate-spin" : ""} />}
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:text-blue-700"
                              title="Sync metrics (data mismatch detected)"
                              loading={isSyncing}
                            >
                              Sync
                            </Button>
                          </Popconfirm>
                        )}
                        {hasOverrides(row.date) && editingDate !== row.date && (
                          <Popconfirm
                            title="Revert all overrides?"
                            description="This will remove all manual overrides and restore calculated values."
                            onConfirm={(e) => {
                              e?.stopPropagation();
                              handleRevert(row.date);
                            }}
                            onCancel={(e) => e?.stopPropagation()}
                            okText="Yes, Revert"
                            cancelText="Cancel"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<RotateCcw size={14} />}
                              onClick={(e) => e.stopPropagation()}
                              className="text-orange-600 hover:text-orange-700"
                              title="Revert overrides"
                            >
                              Revert
                            </Button>
                          </Popconfirm>
                        )}
                        <Button
                          type="text"
                          size="small"
                          icon={editingDate === row.date ? <X size={14} /> : <Edit2 size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (editingDate === row.date) {
                              handleCancel();
                            } else {
                              handleEdit(row.date);
                            }
                          }}
                        >
                          {editingDate === row.date ? "Cancel" : "Edit"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ),
                children: (
                  <div className="pt-4 space-y-4">

                  {/* Client Services Row */}
                  <div className="grid grid-cols-4 gap-0 border-b border-gray-200 pb-4">
                    <div className="text-center border-r border-gray-200 pr-4">
                      <div className="text-xs text-gray-500 mb-1">Transport</div>
                      <div className="font-semibold">{formatCurrency(row.transport.revenue)}</div>
                      <div className="text-xs text-gray-500">{row.transport.count} services</div>
                    </div>
                    <div className="text-center border-r border-gray-200 pr-2 pl-2 bg-gray-100">
                      <div className="text-xs text-gray-700 font-medium mb-1">Site Services</div>
                      <div className="font-semibold text-gray-900">{formatCurrency(row.site.revenue)}</div>
                      <div className="text-xs text-gray-600">{row.site.count} services</div>
                    </div>
                    <div className="text-center border-r border-gray-200 pr-4 pl-2 bg-gray-100">
                      <div className="text-xs text-gray-700 font-medium mb-1">Fabrication</div>
                      <div className="font-semibold text-gray-900">{formatCurrency(row.fabrication.revenue)}</div>
                      <div className="text-xs text-gray-600">{row.fabrication.count} services</div>
                    </div>
                    <div className="text-center pl-4">
                      <div className="text-xs text-gray-500 mb-1">Warehouse</div>
                      <div className="font-semibold">{formatCurrency(row.warehouse.revenue)}</div>
                      <div className="text-xs text-gray-500">{row.warehouse.count} services</div>
                    </div>
                  </div>

                  {/* Costs Row */}
                  <div className="grid grid-cols-4 gap-6">
                    {/* Staff */}
                    <div className="border-r border-gray-200 pr-6">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Staff</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Working:</span>
                          <span className="font-medium">{row.staffWorking}</span>
                        </div>
                        <div className="flex justify-between text-sm items-center">
                          <span className="text-gray-600">Hours:</span>
                          {editingDate === row.date ? (
                            <InputNumber
                              value={overrideValues.staffHours ?? undefined}
                              onChange={(val) => setOverrideValues({ ...overrideValues, staffHours: val ?? null })}
                              min={0}
                              step={0.1}
                              precision={1}
                              size="small"
                              style={{ width: 80 }}
                            />
                          ) : (
                            <span className="font-medium">{row.staffHours.toFixed(1)}</span>
                          )}
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Cost:</span>
                          <span className="font-medium">{formatCurrency(row.staffCost)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Vessels */}
                    <div className="border-r border-gray-200 pr-6">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Vessels</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-medium">{row.totalVessels}</span>
                        </div>
                        <div className="flex justify-between text-sm items-center">
                          <span className="text-gray-600">Hours:</span>
                          {editingDate === row.date ? (
                            <InputNumber
                              value={overrideValues.vesselHours ?? undefined}
                              onChange={(val) => setOverrideValues({ ...overrideValues, vesselHours: val ?? null })}
                              min={0}
                              step={0.1}
                              precision={1}
                              size="small"
                              style={{ width: 80 }}
                            />
                          ) : (
                            <span className="font-medium">{row.vesselHours.toFixed(1)}</span>
                          )}
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Cost:</span>
                          <span className="font-medium">{formatCurrency(row.vesselCost)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Materials */}
                    <div className="border-r border-gray-200 pr-6">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Materials</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Charges:</span>
                          <span className="font-medium">{formatCurrency(row.materialCharges)}</span>
                        </div>
                        <div className="flex justify-between text-sm items-center">
                          <span className="text-gray-600">Costs:</span>
                          {editingDate === row.date ? (
                            <InputNumber
                              value={overrideValues.materialsCost ?? undefined}
                              onChange={(val) => setOverrideValues({ ...overrideValues, materialsCost: val ?? null })}
                              min={0}
                              step={1}
                              precision={2}
                              size="small"
                              style={{ width: 100 }}
                              prefix="$"
                            />
                          ) : (
                            <span className="font-medium">{formatCurrency(row.materialCosts)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Expenses & Commissions */}
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">Other Expenses</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Additional:</span>
                          <span className="font-medium">
                            {formatCurrency(datePeriod === "day" ? otherExpenses : otherExpenses / dates.length)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Commissions:</span>
                          <span className="font-medium">{formatCurrency(0)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-1 border-t border-gray-200">
                          <span className="text-gray-700 font-medium">Total Other:</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(datePeriod === "day" ? otherExpenses : otherExpenses / dates.length)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Save/Revert buttons when editing */}
                  {editingDate === row.date && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                      {hasOverrides(row.date) && (
                        <Popconfirm
                          title="Revert all overrides?"
                          description="This will remove all manual overrides and restore calculated values."
                          onConfirm={() => handleRevert(row.date)}
                          okText="Yes, Revert"
                          cancelText="Cancel"
                        >
                          <Button
                            type="default"
                            danger
                            icon={<RotateCcw size={14} />}
                            loading={updateOverridesMutation.isPending}
                          >
                            Revert All
                          </Button>
                        </Popconfirm>
                      )}
                      <div className="flex gap-2">
                        <Button
                          onClick={handleCancel}
                          disabled={updateOverridesMutation.isPending}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          icon={<Save size={14} />}
                          onClick={() => handleSave(row.date)}
                          loading={updateOverridesMutation.isPending}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                  </div>
                ),
              }))}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
